
import { useState, useEffect, useCallback } from 'react';
import { supabase, Availability } from '../../../lib/supabase';

type StatusType = 'available' | 'busy' | 'tentative';

interface DayData {
  date: string;
  status: StatusType;
  note: string;
  id?: string;
}

const STATUS_CONFIG: Record<StatusType, { label: string; color: string; bg: string; border: string; icon: string }> = {
  available: {
    label: 'Disponible',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500',
    border: 'border-emerald-500',
    icon: 'ri-checkbox-circle-fill',
  },
  busy: {
    label: 'Ocupado',
    color: 'text-red-400',
    bg: 'bg-red-500',
    border: 'border-red-500',
    icon: 'ri-close-circle-fill',
  },
  tentative: {
    label: 'Tentativo',
    color: 'text-amber-400',
    bg: 'bg-amber-500',
    border: 'border-amber-500',
    icon: 'ri-question-fill',
  },
};

const DAYS_OF_WEEK = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

export default function AvailabilityManager() {
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [availability, setAvailability] = useState<Record<string, DayData>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [editNote, setEditNote] = useState('');
  const [editStatus, setEditStatus] = useState<StatusType>('available');
  const [saveMsg, setSaveMsg] = useState<'success' | 'error' | null>(null);

  const fetchAvailability = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('availability')
      .select('*');
    if (data) {
      const map: Record<string, DayData> = {};
      (data as Availability[]).forEach((item) => {
        map[item.date] = {
          date: item.date,
          status: item.status,
          note: item.note || '',
          id: item.id,
        };
      });
      setAvailability(map);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1;
  };

  const formatDate = (year: number, month: number, day: number) => {
    const m = String(month + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${year}-${m}-${d}`;
  };

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
    setSelectedDay(null);
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
    setSelectedDay(null);
  };

  const handleDayClick = (dateStr: string) => {
    setSelectedDay(dateStr);
    const existing = availability[dateStr];
    setEditStatus(existing?.status || 'available');
    setEditNote(existing?.note || '');
  };

  const handleSaveDay = async () => {
    if (!selectedDay) return;
    setSaving(true);
    setSaveMsg(null);

    const existing = availability[selectedDay];
    let error = null;

    if (existing?.id) {
      const { error: e } = await supabase
        .from('availability')
        .update({ status: editStatus, note: editNote })
        .eq('id', existing.id);
      error = e;
    } else {
      const { error: e } = await supabase
        .from('availability')
        .insert([{ date: selectedDay, status: editStatus, note: editNote }]);
      error = e;
    }

    if (!error) {
      setAvailability((prev) => ({
        ...prev,
        [selectedDay]: {
          ...prev[selectedDay],
          date: selectedDay,
          status: editStatus,
          note: editNote,
        },
      }));
      setSaveMsg('success');
      setTimeout(() => setSaveMsg(null), 2000);
    } else {
      setSaveMsg('error');
    }
    setSaving(false);
  };

  const handleDeleteDay = async () => {
    if (!selectedDay) return;
    const existing = availability[selectedDay];
    if (!existing?.id) {
      setSelectedDay(null);
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from('availability')
      .delete()
      .eq('id', existing.id);

    if (!error) {
      setAvailability((prev) => {
        const next = { ...prev };
        delete next[selectedDay];
        return next;
      });
      setSelectedDay(null);
    }
    setSaving(false);
  };

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
  const todayStr = formatDate(today.getFullYear(), today.getMonth(), today.getDate());

  const stats = Object.values(availability).reduce(
    (acc, d) => {
      acc[d.status] = (acc[d.status] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-white">Calendario de Disponibilidad</h2>
        <p className="text-gray-500 text-sm mt-1">
          Gestiona tu disponibilidad. Los visitantes podrán ver qué fechas tienes libres.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {(Object.keys(STATUS_CONFIG) as StatusType[]).map((s) => (
          <div key={s} className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex items-center gap-3">
            <div className={`w-10 h-10 flex items-center justify-center rounded-full ${STATUS_CONFIG[s].bg}/20`}>
              <i className={`${STATUS_CONFIG[s].icon} ${STATUS_CONFIG[s].color} text-xl`}></i>
            </div>
            <div>
              <p className="text-white font-bold text-xl">{stats[s] || 0}</p>
              <p className="text-gray-500 text-xs">{STATUS_CONFIG[s].label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2 bg-gray-900 border border-gray-800 rounded-xl p-6">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={prevMonth}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-black border border-gray-700 text-gray-400 hover:text-white hover:border-cyan-400 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            <h3 className="text-white font-bold text-lg">
              {MONTHS[currentMonth]} {currentYear}
            </h3>
            <button
              onClick={nextMonth}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-black border border-gray-700 text-gray-400 hover:text-white hover:border-cyan-400 transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </div>

          {/* Days of week */}
          <div className="grid grid-cols-7 mb-2">
            {DAYS_OF_WEEK.map((d) => (
              <div key={d} className="text-center text-gray-500 text-xs font-semibold uppercase py-2">
                {d}
              </div>
            ))}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} />
            ))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dateStr = formatDate(currentYear, currentMonth, day);
              const data = availability[dateStr];
              const isToday = dateStr === todayStr;
              const isSelected = dateStr === selectedDay;
              const isPast = dateStr < todayStr;

              return (
                <button
                  key={dateStr}
                  onClick={() => handleDayClick(dateStr)}
                  className={`
                    relative aspect-square flex flex-col items-center justify-center rounded-lg text-sm font-medium transition-all cursor-pointer
                    ${isSelected ? 'ring-2 ring-cyan-400 ring-offset-1 ring-offset-gray-900' : ''}
                    ${isPast ? 'opacity-40' : 'hover:bg-gray-800'}
                    ${isToday ? 'border border-cyan-400/60' : 'border border-transparent'}
                    ${data ? '' : 'text-gray-400'}
                  `}
                  style={{
                    backgroundColor: data
                      ? data.status === 'available'
                        ? 'rgba(16,185,129,0.15)'
                        : data.status === 'busy'
                        ? 'rgba(239,68,68,0.15)'
                        : 'rgba(245,158,11,0.15)'
                      : undefined,
                  }}
                >
                  <span
                    className={`text-sm font-semibold ${
                      data
                        ? data.status === 'available'
                          ? 'text-emerald-300'
                          : data.status === 'busy'
                          ? 'text-red-300'
                          : 'text-amber-300'
                        : isToday
                        ? 'text-cyan-400'
                        : 'text-gray-300'
                    }`}
                  >
                    {day}
                  </span>
                  {data && (
                    <div
                      className={`w-1.5 h-1.5 rounded-full mt-0.5 ${STATUS_CONFIG[data.status].bg}`}
                    />
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-6 mt-6 pt-4 border-t border-gray-800">
            {(Object.keys(STATUS_CONFIG) as StatusType[]).map((s) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${STATUS_CONFIG[s].bg}`}></div>
                <span className="text-gray-400 text-xs">{STATUS_CONFIG[s].label}</span>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-gray-700 border border-gray-500"></div>
              <span className="text-gray-400 text-xs">Sin definir</span>
            </div>
          </div>
        </div>

        {/* Edit panel */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 flex flex-col">
          {selectedDay ? (
            <>
              <div className="mb-5">
                <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Fecha seleccionada</p>
                <p className="text-white font-bold text-lg">
                  {new Date(selectedDay + 'T12:00:00').toLocaleDateString('es-ES', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </p>
              </div>

              <div className="mb-5">
                <p className="text-gray-400 text-xs uppercase tracking-wider mb-3">Estado</p>
                <div className="space-y-2">
                  {(Object.keys(STATUS_CONFIG) as StatusType[]).map((s) => (
                    <button
                      key={s}
                      onClick={() => setEditStatus(s)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-all cursor-pointer ${
                        editStatus === s
                          ? `${STATUS_CONFIG[s].border} bg-black/50`
                          : 'border-gray-700 hover:border-gray-600'
                      }`}
                    >
                      <i className={`${STATUS_CONFIG[s].icon} ${STATUS_CONFIG[s].color} text-lg`}></i>
                      <span className={`text-sm font-medium ${editStatus === s ? STATUS_CONFIG[s].color : 'text-gray-400'}`}>
                        {STATUS_CONFIG[s].label}
                      </span>
                      {editStatus === s && (
                        <i className="ri-check-line text-cyan-400 ml-auto"></i>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-5">
                <label className="block text-gray-400 text-xs uppercase tracking-wider mb-2">
                  Nota (opcional)
                </label>
                <textarea
                  value={editNote}
                  onChange={(e) => setEditNote(e.target.value)}
                  rows={3}
                  placeholder="Ej: Boda privada, Festival de verano..."
                  className="w-full px-3 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400 transition-all resize-none"
                />
              </div>

              {saveMsg === 'success' && (
                <div className="flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/40 rounded-lg px-3 py-2 text-emerald-400 text-xs mb-3">
                  <i className="ri-checkbox-circle-line"></i> Guardado correctamente
                </div>
              )}
              {saveMsg === 'error' && (
                <div className="flex items-center gap-2 bg-red-500/20 border border-red-500/40 rounded-lg px-3 py-2 text-red-400 text-xs mb-3">
                  <i className="ri-error-warning-line"></i> Error al guardar
                </div>
              )}

              <div className="flex gap-2 mt-auto">
                <button
                  onClick={handleSaveDay}
                  disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-cyan-500 to-fuchsia-500 text-white font-bold text-sm rounded-lg hover:shadow-[0_0_20px_rgba(6,182,212,0.4)] transition-all disabled:opacity-50 cursor-pointer whitespace-nowrap"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <><i className="ri-save-line"></i> Guardar</>
                  )}
                </button>
                {availability[selectedDay]?.id && (
                  <button
                    onClick={handleDeleteDay}
                    disabled={saving}
                    className="w-10 h-10 flex items-center justify-center bg-red-500/20 hover:bg-red-500/40 border border-red-500/40 text-red-400 rounded-lg transition-all cursor-pointer"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center gap-4">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-800">
                <i className="ri-calendar-check-line text-gray-500 text-3xl"></i>
              </div>
              <div>
                <p className="text-gray-400 text-sm font-medium">Selecciona un día</p>
                <p className="text-gray-600 text-xs mt-1">Haz clic en cualquier día del calendario para editar su disponibilidad</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
